import type { MediaView } from '@cobliteam/common-types';
import { Alert, Menu } from '@cobliteam/ui/components';
import Icon from '@cobliteam/ui/icons';
import { format } from '@cobliteam/utils/date';
import axios from 'axios';
import JSZip from 'jszip';
import { useState } from 'react';

interface MenuItemDownloadMediaProps {
  mediaView: MediaView;
}

const MenuItemDownloadMedia = ({ mediaView }: MenuItemDownloadMediaProps) => {
  const [isDownloading, setIsDownloading] = useState(false);

  const { mediaUrl, videos, mediaInfo } = mediaView;
  const { mediaType, licensePlate, timestamp } = mediaInfo;

  const plateSuffix = licensePlate ? `_${licensePlate}` : '';
  const dateSuffix = timestamp ? `_${format(timestamp, 'dd-MM-yyyy_HH-mm')}` : '';
  const fileName = `${mediaType}${plateSuffix}${dateSuffix}`;

  const downloadSingleVideo = async () => {
    if (!mediaUrl) {
      Alert.error('Vídeo não disponível para download');
      return;
    }

    try {
      setIsDownloading(true);

      const response = await fetch(mediaUrl);
      const blob = await response.blob();

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${fileName}.mp4`;

      link.click();
      Alert.success('Vídeo baixado com sucesso!');
    } catch (_error) {
      Alert.error('Erro ao baixar o vídeo. Tente novamente.');
    } finally {
      setIsDownloading(false);
    }
  };

  const downloadMultipleVideos = async () => {
    if (!videos || videos.length === 0) {
      Alert.error('Vídeos não disponíveis para download');
      return;
    }

    try {
      setIsDownloading(true);
      const zip = new JSZip();

      for (const video of videos) {
        const response = await axios.get(video.url, {
          responseType: 'blob',
          headers: {
            'Content-Type': 'application/octet-stream',
            'Cache-Control': 'no-cache',
          },
        });
        const subFileName = `${fileName}_${video.channel}.mp4`;
        zip.file(subFileName, response.data);
      }

      const zipData = await zip.generateAsync({
        type: 'blob',
        streamFiles: true,
      });

      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(zipData);
      link.download = `${fileName}.zip`;
      link.click();
      Alert.success('Vídeos baixados com sucesso!');
    } catch (_error) {
      Alert.error('Erro ao baixar os vídeos. Por favor, tente novamente mais tarde.');
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <>
      <Menu.Item onClick={downloadSingleVideo} className='w-full' disabled={isDownloading}>
        <Menu.Icon icon={<Icon name={isDownloading ? 'cb-loading-spin' : 'grid-2x2'} />} />
        <Menu.Label>Baixar vídeo único</Menu.Label>
      </Menu.Item>
      <Menu.Item onClick={downloadMultipleVideos} className='w-full' disabled={!videos || videos.length === 0 || isDownloading}>
        <Menu.Icon icon={<Icon name={isDownloading ? 'cb-loading-spin' : 'layout-grid'} />} />
        <Menu.Label>Baixar vídeos separados</Menu.Label>
      </Menu.Item>
    </>
  );
};

export default MenuItemDownloadMedia;
